// Theme-aware style utilities
// These provide conditional styling based on the active theme

import clsx from 'clsx';

/**
 * Base styles that are shared across both themes
 */
export const baseStyles = {
    // Layout
    flexCenter: 'flex items-center justify-center',
    flexBetween: 'flex items-center justify-between',

    // Typography
    fontBold: 'font-bold',
    fontExtrabold: 'font-extrabold',
    textSmall: 'text-sm',
    textBase: 'text-base',

    // Transitions
    transition: 'transition-all duration-200 ease-out',
    transitionFast: 'transition-all duration-150 ease-out',
};

/**
 * Theme-specific style maps
 * These get applied based on body class: .theme-neobrutalism or .theme-liquid-glass
 */
export const themeStyles = {
    // Border styles
    border: {
        neobrutalism: 'border-2 border-black',
        liquidGlass: 'border border-white/20',
    },

    // Border radius
    radius: {
        neobrutalism: 'rounded-xl',
        liquidGlass: 'rounded-3xl',
    },
    radiusSmall: {
        neobrutalism: 'rounded-lg',
        liquidGlass: 'rounded-2xl',
    },

    // Background surfaces
    surface: {
        neobrutalism: 'bg-white',
        liquidGlass: 'bg-white/10 backdrop-blur-xl',
    },
    surfaceElevated: {
        neobrutalism: 'bg-white',
        liquidGlass: 'bg-white/15 backdrop-blur-2xl',
    },

    // Primary button
    primaryButton: {
        neobrutalism: 'bg-emerald-500 text-white hover:bg-emerald-400 active:bg-emerald-600',
        liquidGlass: 'bg-violet-500/80 text-white hover:bg-violet-400/80 active:bg-violet-600/80 backdrop-blur-sm',
    },

    // Secondary button
    secondaryButton: {
        neobrutalism: 'bg-gray-100 text-gray-900 hover:bg-gray-50 active:bg-gray-200',
        liquidGlass: 'bg-white/10 text-white hover:bg-white/20 active:bg-white/5',
    },

    // Ghost button
    ghostButton: {
        neobrutalism: 'bg-transparent text-slate-700 border-transparent hover:border-black hover:bg-gray-50',
        liquidGlass: 'bg-transparent text-white/80 border-transparent hover:bg-white/10',
    },

    // Outline button
    outlineButton: {
        neobrutalism: 'bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100',
        liquidGlass: 'bg-transparent text-white border-white/30 hover:bg-white/10 active:bg-white/5',
    },

    // Danger button
    dangerButton: {
        neobrutalism: 'bg-red-600 text-white hover:bg-red-500 active:bg-red-700',
        liquidGlass: 'bg-rose-500/80 text-white hover:bg-rose-400/80 active:bg-rose-600/80',
    },

    // Text colors
    textPrimary: {
        neobrutalism: 'text-slate-900',
        liquidGlass: 'text-white',
    },
    textSecondary: {
        neobrutalism: 'text-slate-600',
        liquidGlass: 'text-white/70',
    },
    textMuted: {
        neobrutalism: 'text-slate-500',
        liquidGlass: 'text-white/50',
    },

    // Shadow effects
    shadowHover: {
        neobrutalism: 'hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
        liquidGlass: 'hover:shadow-[0_8px_32px_rgba(139,92,246,0.2)]',
    },

    // Hover transforms
    hoverLift: {
        neobrutalism: 'hover:-translate-x-1 hover:-translate-y-1',
        liquidGlass: 'hover:-translate-y-0.5',
    },

    // Active states
    activePress: {
        neobrutalism: 'active:translate-x-0 active:translate-y-0 active:shadow-none',
        liquidGlass: 'active:translate-y-0 active:shadow-[0_4px_16px_rgba(0,0,0,0.15)]',
    },

    // Focus ring
    focusRing: {
        neobrutalism: 'focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2',
        liquidGlass: 'focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900',
    },

    // Input styles
    inputBg: {
        neobrutalism: 'bg-slate-50 focus:bg-white',
        liquidGlass: 'bg-white/5 focus:bg-white/10',
    },
};

/**
 * Generate theme-conditional classes
 * Uses CSS selectors like .theme-neobrutalism .class and .theme-liquid-glass .class
 */
export function getThemedClass(
    styleKey: keyof typeof themeStyles,
    additionalClasses?: string
): string {
    const styles = themeStyles[styleKey];

    // Return both with theme-conditional targeting
    // The active theme's body class will activate the right styles
    return clsx(
        // Default (neobrutalism) - always applied, overridden by liquid-glass when active
        styles.neobrutalism,
        // Liquid glass variant - applied via CSS specificity when .theme-liquid-glass is on body
        `[.theme-liquid-glass_&]:${styles.liquidGlass.split(' ').join(' [.theme-liquid-glass_&]:')}`,
        additionalClasses
    );
}

/**
 * Simple theme class merger - for components that need both theme variants inline
 * Returns classes that respond to theme body class
 */
export function themed(styleKey: keyof typeof themeStyles): string {
    const styles = themeStyles[styleKey];
    return `${styles.neobrutalism} theme-liquid-glass:${styles.liquidGlass}`;
}

/**
 * CSS Module-style theme class builder
 * Creates a class string that uses Tailwind's group variant pattern
 */
export const themeClasses = {
    // Card/Surface
    card: clsx(
        // Base structure
        'relative overflow-hidden',
        // Neobrutalism (default)
        'bg-white border-2 border-black rounded-xl',
        // Liquid glass (when .theme-liquid-glass is on an ancestor)
        '[.theme-liquid-glass_&]:bg-white/10',
        '[.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-3xl',
        '[.theme-liquid-glass_&]:backdrop-blur-xl',
    ),

    // Interactive card hover
    cardInteractive: clsx(
        'cursor-pointer',
        // Neobrutalism hover
        'hover:-translate-x-1 hover:-translate-y-1',
        'hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
        'active:translate-x-0 active:translate-y-0 active:shadow-none',
        // Liquid glass hover  
        '[.theme-liquid-glass_&]:hover:translate-x-0',
        '[.theme-liquid-glass_&]:hover:-translate-y-1',
        '[.theme-liquid-glass_&]:hover:shadow-[0_12px_40px_rgba(139,92,246,0.15)]',
        '[.theme-liquid-glass_&]:active:translate-y-0',
    ),

    // Button base
    buttonBase: clsx(
        'relative inline-flex items-center justify-center gap-2',
        'font-bold transition-all duration-200',
        // Neobrutalism
        'border-2 border-black rounded-xl',
        'hover:-translate-x-1 hover:-translate-y-1',
        'hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
        'active:translate-x-0 active:translate-y-0 active:shadow-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
        // Liquid glass
        '[.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-2xl',
        '[.theme-liquid-glass_&]:hover:translate-x-0',
        '[.theme-liquid-glass_&]:hover:-translate-y-0.5',
        '[.theme-liquid-glass_&]:hover:shadow-[0_8px_24px_rgba(139,92,246,0.25)]',
        '[.theme-liquid-glass_&]:focus-visible:ring-violet-400',
        '[.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
    ),

    // Button variants
    buttonPrimary: clsx(
        // Neobrutalism
        'bg-emerald-500 text-white hover:bg-emerald-400 active:bg-emerald-600',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-violet-500/80',
        '[.theme-liquid-glass_&]:hover:bg-violet-400/80',
        '[.theme-liquid-glass_&]:active:bg-violet-600/80',
        '[.theme-liquid-glass_&]:backdrop-blur-sm',
    ),

    buttonSecondary: clsx(
        // Neobrutalism
        'bg-gray-100 text-gray-900 hover:bg-gray-50 active:bg-gray-200',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-white/10',
        '[.theme-liquid-glass_&]:text-white',
        '[.theme-liquid-glass_&]:hover:bg-white/20',
        '[.theme-liquid-glass_&]:active:bg-white/5',
    ),

    buttonOutline: clsx(
        // Neobrutalism
        'bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-transparent',
        '[.theme-liquid-glass_&]:text-white',
        '[.theme-liquid-glass_&]:border-white/30',
        '[.theme-liquid-glass_&]:hover:bg-white/10',
        '[.theme-liquid-glass_&]:active:bg-white/5',
    ),

    buttonGhost: clsx(
        // Neobrutalism
        'bg-transparent text-slate-700 border-transparent hover:border-black hover:bg-gray-50 active:bg-gray-100',
        // Liquid glass
        '[.theme-liquid-glass_&]:text-white/80',
        '[.theme-liquid-glass_&]:hover:border-transparent',
        '[.theme-liquid-glass_&]:hover:bg-white/10',
        '[.theme-liquid-glass_&]:active:bg-white/5',
    ),

    buttonDanger: clsx(
        // Neobrutalism
        'bg-red-600 text-white hover:bg-red-500 active:bg-red-700',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-rose-500/80',
        '[.theme-liquid-glass_&]:hover:bg-rose-400/80',
        '[.theme-liquid-glass_&]:active:bg-rose-600/80',
    ),

    // Modal/overlay backdrop
    backdrop: clsx(
        // Neobrutalism
        'bg-black/50',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-slate-900/60',
        '[.theme-liquid-glass_&]:backdrop-blur-sm',
    ),

    // Modal panel
    modalPanel: clsx(
        'relative flex flex-col',
        // Neobrutalism
        'bg-white border-2 border-black rounded-xl shadow-2xl',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-slate-800/80',
        '[.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-3xl',
        '[.theme-liquid-glass_&]:backdrop-blur-2xl',
        '[.theme-liquid-glass_&]:shadow-[0_24px_64px_rgba(0,0,0,0.4)]',
    ),

    // Text primary
    textPrimary: clsx(
        'text-slate-900',
        '[.theme-liquid-glass_&]:text-white',
    ),

    // Text secondary
    textSecondary: clsx(
        'text-slate-600',
        '[.theme-liquid-glass_&]:text-white/70',
    ),

    // Text muted
    textMuted: clsx(
        'text-slate-500',
        '[.theme-liquid-glass_&]:text-white/50',
    ),

    // Input field
    input: clsx(
        'w-full transition-all duration-150',
        // Neobrutalism
        'border-2 border-black rounded-lg',
        'bg-slate-50 focus:bg-white',
        'focus:ring-2 focus:ring-emerald-500',
        // Liquid glass
        '[.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-xl',
        '[.theme-liquid-glass_&]:bg-white/5',
        '[.theme-liquid-glass_&]:focus:bg-white/10',
        '[.theme-liquid-glass_&]:focus:ring-violet-400',
        '[.theme-liquid-glass_&]:text-white',
        '[.theme-liquid-glass_&]:placeholder:text-white/40',
    ),

    // Badge
    badge: clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold',
        // Neobrutalism
        'border-2 border-black rounded-md shadow-[2px_2px_0_0_rgba(0,0,0,0.8)]',
        // Liquid glass
        '[.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-full',
        '[.theme-liquid-glass_&]:shadow-none',
        '[.theme-liquid-glass_&]:backdrop-blur-sm',
    ),

    // Tooltip
    tooltip: clsx(
        'px-3 py-2 text-sm font-medium',
        // Neobrutalism
        'bg-slate-900 text-white border-2 border-black rounded-lg',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-slate-800/90',
        '[.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-xl',
        '[.theme-liquid-glass_&]:backdrop-blur-xl',
    ),

    // Alert
    alertBase: clsx(
        'relative p-4 flex items-start gap-4',
        // Neobrutalism
        'border-2 border-black rounded-xl',
        // Liquid glass
        '[.theme-liquid-glass_&]:border',
        '[.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-2xl',
        '[.theme-liquid-glass_&]:backdrop-blur-xl',
    ),

    // Table
    tableHeader: clsx(
        'border-b-2 border-black',
        // Neobrutalism
        'bg-slate-50',
        // Liquid glass
        '[.theme-liquid-glass_&]:bg-white/5',
        '[.theme-liquid-glass_&]:border-white/20',
    ),

    tableRow: clsx(
        'border-b border-slate-200',
        // Liquid glass
        '[.theme-liquid-glass_&]:border-white/10',
    ),
};

export default themeClasses;
